// Iter-184/185 — Shared Forfait progress widget used in:
//  - ClientDetail.jsx (under each forfait mission row)
//  - Missions.jsx (global list, inline under mission title)
//
// Renders a compact 3-segment stacked progress bar with live legend:
//   BILLED (green) / PENDING_VALIDATION (amber) / TO_BILL (zinc)
//
// Skips rendering entirely if the mission is not type=FORFAIT or has no milestones.
import React from "react";

export default function ForfaitProgress({ mission, compact = false }) {
  if (!mission || mission.type !== "FORFAIT") return null;
  // Iter-208 — When the viewer cannot see financial data the backend redacts
  // total_amount_ht & milestone percentages → skip the widget entirely to
  // avoid showing "0 / 0 MAD" and to keep the UI honest.
  if (mission._finance_redacted) return null;
  const milestones = mission.milestones || [];
  if (milestones.length === 0) return null;

  const totalAmount = Number(mission.total_amount_ht || 0);
  const currency = mission.currency || "";
  const sumByStatus = (st) => milestones
    .filter((m) => m.status === st)
    .reduce((s, m) => s + (Number(m.percentage) || 0), 0);
  const pctBilled = sumByStatus("BILLED");
  const pctPending = sumByStatus("PENDING_VALIDATION");
  const pctToBill = sumByStatus("TO_BILL");
  const amountBilled = Math.round(totalAmount * pctBilled / 100);
  const fmt = (n) => `${Number(n).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} ${currency}`.trim();

  return (
    <div
      className={compact ? "mt-1" : "mt-2"}
      data-testid={`forfait-progress-${mission.mission_id}`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className={`flex items-center flex-wrap gap-x-2 gap-y-0.5 text-[10px] font-mono text-zinc-500 ${compact ? "leading-tight" : ""}`}>
        {!compact && <span className="uppercase tracking-[0.16em]">Forfait</span>}
        <span className="text-zinc-700">{fmt(amountBilled)} / {fmt(totalAmount)}</span>
        <span className="text-zinc-400">·</span>
        <span className="text-emerald-700" data-testid={`forfait-pct-billed-${mission.mission_id}`}>
          {Math.round(pctBilled)}% facturé
        </span>
        {pctPending > 0 && (
          <>
            <span className="text-zinc-400">·</span>
            <span className="text-amber-700" data-testid={`forfait-pct-pending-${mission.mission_id}`}>
              {Math.round(pctPending)}% en validation
            </span>
          </>
        )}
        {pctToBill > 0 && (
          <>
            <span className="text-zinc-400">·</span>
            <span className="text-zinc-600" data-testid={`forfait-pct-tobill-${mission.mission_id}`}>
              {Math.round(pctToBill)}% à facturer
            </span>
          </>
        )}
      </div>
      <div className={`mt-1 h-1.5 ${compact ? "w-full max-w-[240px]" : "w-full max-w-md"} rounded-full bg-zinc-100 overflow-hidden flex`}>
        {pctBilled > 0 && <div className="bg-emerald-600 h-full" style={{ width: `${pctBilled}%` }} title={`${pctBilled}% facturé`} />}
        {pctPending > 0 && <div className="bg-amber-500 h-full" style={{ width: `${pctPending}%` }} title={`${pctPending}% en validation`} />}
        {pctToBill > 0 && <div className="bg-zinc-300 h-full" style={{ width: `${pctToBill}%` }} title={`${pctToBill}% à facturer`} />}
      </div>
    </div>
  );
}
